import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { setPreferredCaseId } from '@/global/preferredCase'
import bus from '@/global/bus'

// Export routes for testing
/**
 * Array of route records for the application's Vue Router.
 *
 * Each route object typically contains:
 *   - path {string}: URL pattern (many routes include a dynamic ":company" segment).
 *   - name {string}: unique route name.
 *   - component {Function|Object}: component or lazy-loaded component factory (dynamic import).
 *   - meta {Object}: arbitrary metadata (e.g., { requiresAuth: true }).
 *
 * Special behaviors:
 *   - Company-scoped routes use '/:company/...' and include login, verification (link, BankID, MitID),
 *     contact, and case listing/detail pages.
 *   - No-company route '/' renders a landing view when no company context is present.
 *   - Catch-all route '/:pathMatch(.*)*' maps unmatched URLs to a 404 view.
 *
 * Intended usage:
 *   Import this array into Vue Router configuration to register client-side routes and to drive
 *   navigation guards based on route.meta (e.g., requiresAuth).
 *
 * @constant {Array<Object>} routes
 */
export const routes = [
  // Impersonation route
  {
    path: '/impersonate/:guid/:secret',
    name: 'impersonation-verify',
    component: () => import('@/views/VerifyImpersonationView.vue'),
  },
  // Company-specific routes
  {
    path: '/:company/login',
    name: 'company-login',
    component: () => import('@/views/LoginView.vue'),
  },
  {
    path: '/:company/verify/link',
    name: 'company-verify',
    component: () => import('@/views/VerifyView.vue'),
  },
  {
    path: '/:company/verify/bankid',
    name: 'company-bankid-verify',
    component: () => import('@/views/BankIdVerifyView.vue'),
  },
  {
    path: '/:company/verify/mitid',
    name: 'company-mitid-verify',
    component: () => import('@/views/MitIdVerifyView.vue'),
  },
  {
    path: '/:company/contact',
    name: 'company-contact',
    component: () => import('@/views/ContactView.vue'),
  },
  {
    path: '/:company/cases',
    name: 'company-cases',
    component: () => import('@/views/CasesView.vue'),
    meta: { requiresAuth: true },
    props: { archived: false },
  },
  // Archived cases route (reuses CasesView with archived flag)
  {
    path: '/:company/cases/archived',
    name: 'company-cases-archived',
    component: () => import('@/views/CasesView.vue'),
    meta: { requiresAuth: true },
    props: { archived: true },
  },
  // Case detail route (standalone, not part of wizard)
  {
    path: '/:company/cases/:caseId',
    name: 'company-case-details',
    component: () => import('@/views/CaseDetailView.vue'),
    meta: { requiresAuth: true },
  },
  // Wizard flow routes
  // WizardView wraps all wizard step routes and provides step navigation
  {
    path: '/:company/cases/:caseId',
    name: 'wizard',
    component: () => import('@/views/WizardView.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'contact-details',
        name: 'contact-details',
        component: () => import('@/views/ContactDetailsView.vue'),
      },
      {
        path: 'vehicle-information',
        name: 'vehicle-information',
        component: () => import('@/views/VehicleInformationView.vue'),
      },
      {
        path: 'power-of-attorney',
        name: 'power-of-attorney',
        component: () => import('@/views/PowerOfAttorneyView.vue'),
      },
      {
        path: 'compensation-offer',
        name: 'compensation-offer',
        component: () => import('@/views/CompensationOfferView.vue'),
      },
      {
        path: 'banking-details',
        name: 'banking-details',
        component: () => import('@/views/BankingDetailsView.vue'),
      },
      {
        path: 'rating',
        name: 'rating',
        component: () => import('@/views/RatingView.vue'),
      },
    ],
  },
  // Redirect company root to login
  {
    path: '/:company',
    redirect: (to) => {
      return { name: 'company-login', params: { company: to.params.company } }
    },
  },
  // No company context route
  {
    path: '/',
    name: 'no-company',
    component: () => import('@/views/NoCompanyView.vue'),
  },
  // Error pages (no company parameter required)
  {
    path: '/:pathMatch(.*)*', // catch-all if none of the previous routes match
    name: 'not-found',
    component: () => import('@/views/404View.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

// Navigation guard to handle theming before route loads
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const themeStore = useThemeStore()
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)

  // This is a temporary code, will be removed during polish
  // Check for theme query parameter first (e.g., ?theme=lf)
  const themeParam = to.query.theme
  const company = to.params.company

  try {
    if (themeParam === 'lf') {
      // Use LF theme when ?theme=lf is present
      await themeStore.loadAndApplyTheme('lf')
    } else if (themeParam === 'leasing') {
      // Use Leasing theme when ?theme=leasing is present
      await themeStore.loadAndApplyTheme('leasing')
    } else if (themeParam === 'svedea') {
      // Use Svedea theme when ?theme=svedea is present
      await themeStore.loadAndApplyTheme('svedea')
    } else if (themeParam === 'gjensidige') {
      // Use Gjensidige theme when ?theme=gjensidige is present
      await themeStore.loadAndApplyTheme('gjensidige')
    } else if (company) {
      // Load theme for the company (this will handle API call and fallback)
      await themeStore.loadAndApplyTheme(company)
    } else {
      // No company in URL, use default theme
      themeStore.resetToDefault()
    }
  } catch (error) {
    // If company doesn't exist (404), redirect to not-found page
    if (error.response?.status === 404) {
      next({ name: 'not-found' })
      return
    }
    // For other errors, continue with default theme
    console.error('Error loading theme:', error)
    themeStore.resetToDefault()
  }

  // Handle authentication
  if (requiresAuth && !authStore.isLoggedIn) {
    // Remember the preferred deep link (e.g. /:company/cases/:caseId or a
    // wizard step) so the user can be taken directly there after logging in.
    // The company is stored alongside the id so the value is only replayed for
    // the same company.
    if (to.params.caseId) {
      setPreferredCaseId(to.params.caseId, company, to.name)
    }
    // Always redirect to company-login since we only have company routes
    if (company) {
      next({ name: 'company-login', params: { company } })
    } else {
      // No company in URL, redirect to no-company view
      next({ name: 'no-company' })
    }
  } else {
    next()
  }
})

bus.on('auth-logout', () => {
  // Redirect to 404 or handle logout differently since we don't have a default login
  // The application should handle this by redirecting to a company-specific login
  const currentRoute = router.currentRoute.value
  const company = currentRoute.params.company

  if (company) {
    router.push({ name: 'company-login', params: { company } })
  } else {
    // No company context, redirect to no-company view
    router.push({ name: 'no-company' })
  }
})

export default router
